import { LicenseType } from './school-license.model';

/**
 * Ceník z mapavyuky.cz/#cenik. Nepřichází z API, takže se při změně cen na webu musí přepsat tady
 * — je to jediné místo, odkud ceny čte jak přehled na stránce licencí, tak objednávka v chatu.
 *
 * Zkušební verze tu schválně není. Na webu je to nabídka pro nové zájemce; škola, která už
 * v systému je, si ji objednat nemůže.
 */

/** Pásmo velikosti školy, podle kterého se odvíjí cena. */
export type SchoolSizeBand = 'upTo50' | 'upTo300' | 'over300';

export interface SizeOption {
  band: SchoolSizeBand;
  label: string;
}

export const SIZE_OPTIONS: readonly SizeOption[] = [
  { band: 'upTo50', label: 'do 50 žáků' },
  { band: 'upTo300', label: '51–300 žáků' },
  { band: 'over300', label: '301 a více žáků' },
];

export const DEFAULT_SIZE_BAND: SchoolSizeBand = 'upTo50';

export interface PricingModule {
  code: string;
  name: string;
  /** Co modul umí, slovy ceníku — v přehledu se ukazuje pod názvem. */
  description: string;
  includedIn: readonly LicenseType[];
}

export interface PricingTier {
  type: LicenseType;
  /** Roční cena vč. DPH v korunách, pro každé pásmo velikosti školy. */
  prices: Record<SchoolSizeBand, number>;
  highlights: string[];
}

/** Tarify, které si škola může objednat, v pořadí od nejlevnějšího. */
export const PRICING_TIERS: readonly PricingTier[] = [
  {
    type: 'start',
    prices: { upTo50: 19_990, upTo300: 24_990, over300: 29_990 },
    highlights: [
      '1 výchozí šablona titulní strany',
      '5 000 AI kreditů ročně',
      'Odpověď do 14 dnů',
      'Základní školení',
      'Pouze jedna škola pod jedním účtem',
    ],
  },
  {
    type: 'premium',
    prices: { upTo50: 29_990, upTo300: 34_990, over300: 39_990 },
    highlights: [
      'Výběr ze 6 šablon titulní strany',
      '10 000 AI kreditů ročně',
      'Prioritní online konzultace',
      'Rozšiřující školení',
      'Více škol pod jedním účtem',
      'Nastavení AI',
    ],
  },
];

/** Tarify, které si škola může objednat — zkušební licenci uděluje jen správce systému. */
export const ORDERABLE_TIERS: readonly LicenseType[] = PRICING_TIERS.map((tier) => tier.type);

/**
 * Moduly podle ceníku. Tarify se liší v AI asistentovi (M4), dílčích exportech (M10) a nastavení AI
 * (M14) — proto je přehled tabulkou a ne dvěma seznamy, ve kterých by se ten rozdíl ztratil.
 */
export const PRICING_MODULES: readonly PricingModule[] = [
  {
    code: 'M1',
    name: 'Tvorba učebních plánů',
    description:
      'Přehledné sestavení a správa týdenní i celkové hodinové dotace pro jednotlivé předměty a ročníky v souladu s požadavky RVP.',
    includedIn: ORDERABLE_TIERS,
  },
  {
    code: 'M2',
    name: 'Tvorba tematických plánů (bloky)',
    description:
      'Rozplánování učiva v předmětu do tematických bloků, které strukturují výuku během celého školního roku.',
    includedIn: ORDERABLE_TIERS,
  },
  {
    code: 'M3',
    name: 'Generování tematických bloků pomocí AI',
    description:
      'Chytrý asistent na základě vašeho zadání automaticky navrhne znění tematického bloku včetně cíle a učiva.',
    includedIn: ORDERABLE_TIERS,
  },
  {
    code: 'M4',
    name: 'AI asistent při tvorbě bloků',
    description:
      'AI asistent tvoří a upravuje tematický blok podle vašich požadavků. Pomáhá blok doplnit, výsledný text pak můžete upravit.',
    includedIn: ['premium'],
  },
  {
    code: 'M5',
    name: 'Mapování OVU pomocí AI',
    description:
      'Umělá inteligence analyzuje tematický blok a navrhne nejvhodnější Očekávané výsledky učení (OVU), které daná výuka pokrývá.',
    includedIn: ORDERABLE_TIERS,
  },
  {
    code: 'M6',
    name: 'Vytvoření ŠOVU',
    description:
      'Definování a správa Školních očekávaných výsledků učení (ŠOVU), tedy vlastních výstupů přizpůsobených potřebám vaší školy.',
    includedIn: ORDERABLE_TIERS,
  },
  {
    code: 'M7',
    name: 'Generování ŠOVU pomocí AI',
    description:
      'AI na základě obsahu tematického bloku formuluje návrhy konkrétních Školních očekávaných výsledků učení (ŠOVU).',
    includedIn: ORDERABLE_TIERS,
  },
  {
    code: 'M8',
    name: 'Vazby OVU na ŠOVU',
    description:
      'Zajišťuje přehlednou vazbu mezi oficiálními výstupy (OVU) a vašimi školními výstupy (ŠOVU).',
    includedIn: ORDERABLE_TIERS,
  },
  {
    code: 'M9',
    name: 'Analýza pokrytí OVU',
    description:
      'Automatická kontrola v podobě přehledných grafů, kde vidíte nepokryté nebo naopak nadbytečně zastoupené OVU v rámci celého ŠVP.',
    includedIn: ORDERABLE_TIERS,
  },
  {
    code: 'M10',
    name: 'Dílčí exporty XLSX/PDF',
    description:
      'Stahování samostatných podkladů a tabulek pro konkrétní předměty (učební plán, tematické bloky, přehled předmětů) ve formátu Excel nebo PDF.',
    includedIn: ['premium'],
  },
  {
    code: 'M11',
    name: 'Generování obecných částí ŠVP pomocí AI',
    description:
      'Pomáhá s formulací úvodních a obecných kapitol ŠVP (profil školy, profily předmětů apod.).',
    includedIn: ORDERABLE_TIERS,
  },
  {
    code: 'M12',
    name: 'Plný export ŠVP v PDF',
    description:
      'Generuje kompletní, finální dokument Školního vzdělávacího programu v PDF připravený ke schválení a publikaci.',
    includedIn: ORDERABLE_TIERS,
  },
  {
    code: 'M14',
    name: 'Nastavení AI',
    description:
      'Výběr AI modelu, míry kreativity a vlastních pokynů pro generování tematických bloků, ŠOVU a dalších částí ŠVP.',
    includedIn: ['premium'],
  },
];

const PRICE_FORMATTER = new Intl.NumberFormat('cs-CZ');

export function sizeLabel(band: SchoolSizeBand): string {
  return SIZE_OPTIONS.find((option) => option.band === band)?.label ?? '';
}

/** Roční cena tarifu pro dané pásmo, i s jednotkou — text je všude stejný. */
export function licensePrice(type: LicenseType, band: SchoolSizeBand): string {
  const tier = PRICING_TIERS.find((candidate) => candidate.type === type);
  if (tier === undefined) return '';
  return `${PRICE_FORMATTER.format(tier.prices[band])} Kč vč. DPH / rok`;
}

/** Velikost jednoho balíčku AI kreditů, které si škola může přikoupit nad rámec licence. */
export const CREDIT_PACKAGE_SIZE = 5_000;

/** Cena jednoho balíčku vč. DPH v korunách. */
export const CREDIT_PACKAGE_PRICE = 2_500;

/** Kolik balíčků jde v objednávce vybrat. Větší odběr se řeší individuálně e-mailem. */
export const CREDIT_PACKAGE_OPTIONS: readonly number[] = [1, 2, 3];

/** Počet kreditů v objednávce i s jednotkou, např. „10 000 kreditů“. */
export function creditAmountLabel(packages: number): string {
  return `${PRICE_FORMATTER.format(CREDIT_PACKAGE_SIZE * packages)} kreditů`;
}

/** Cena za objednaný počet balíčků i s jednotkou — text je všude stejný. */
export function creditPackagePrice(packages: number): string {
  return `${PRICE_FORMATTER.format(CREDIT_PACKAGE_PRICE * packages)} Kč vč. DPH`;
}

/** Popisek volby v nabídce, včetně české shody čísla s podstatným jménem. */
export function creditPackageLabel(packages: number): string {
  const noun = packages === 1 ? 'balíček' : packages < 5 ? 'balíčky' : 'balíčků';
  return `${packages} ${noun} — ${creditAmountLabel(packages)}`;
}
